/**
 * Command Palette
 *
 * A Linear/Raycast-style command palette for quick navigation and actions.
 * Built on cmdk for keyboard navigation and fuzzy search.
 *
 * Features:
 * - Search across projects, issues, users
 * - Quick actions (create, navigate)
 * - Recent items
 * - Keyboard navigation
 */

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Command } from 'cmdk';
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Icon } from '@/components/ui/icon';
import { useGlobalSearch } from '@/hooks/use-global-search';
import { getModifierSymbol } from '@/hooks/use-keyboard-shortcuts';
import { overlayVariants, prefersReducedMotion, scaleVariants } from '@/lib/motion';
import { cn } from '@/lib/utils';

// =============================================================================
// Types
// =============================================================================

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreateIssue?: () => void;
  onCreateProject?: () => void;
}

type CommandPage = 'home' | 'search';

// =============================================================================
// Helper Functions
// =============================================================================

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function getStatusColor(status: string): string {
  switch (status) {
    case 'OPEN':
      return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
    case 'IN_PROGRESS':
      return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
    case 'RESOLVED':
      return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400';
    case 'CLOSED':
      return 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400';
    default:
      return 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400';
  }
}

// =============================================================================
// Command Item Components
// =============================================================================

function CommandItem({
  children,
  onSelect,
  className,
}: {
  children: React.ReactNode;
  onSelect: () => void;
  className?: string;
}) {
  return (
    <Command.Item
      onSelect={onSelect}
      className={cn(
        'flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-sm',
        'data-[selected=true]:bg-accent',
        'transition-colors',
        className,
      )}
    >
      {children}
    </Command.Item>
  );
}

function CommandGroup({ heading, children }: { heading: string; children: React.ReactNode }) {
  return (
    <Command.Group
      heading={heading}
      className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-muted-foreground"
    >
      {children}
    </Command.Group>
  );
}

function CommandShortcut({ children }: { children: React.ReactNode }) {
  return <span className="ml-auto text-xs text-muted-foreground">{children}</span>;
}

function IconContainer({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg', className)}>
      {children}
    </div>
  );
}

// =============================================================================
// Main Command Palette Component
// =============================================================================

export function CommandPalette({
  open,
  onOpenChange,
  onCreateIssue,
  onCreateProject,
}: CommandPaletteProps) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const shouldAnimate = !prefersReducedMotion();
  const modKey = getModifierSymbol();

  const [inputValue, setInputValue] = useState('');

  const { setQuery, results, isLoading, hasResults, resetQuery } = useGlobalSearch();

  // Derive page from inputValue instead of storing in state
  const page: CommandPage = inputValue.length >= 2 ? 'search' : 'home';

  // Handle input change and trigger search
  const handleInputChange = useCallback(
    (value: string) => {
      setInputValue(value);
      if (value.length >= 2) {
        setQuery(value);
      } else if (value.length === 0) {
        resetQuery();
      }
    },
    [setQuery, resetQuery],
  );

  // Handle dialog open/close - reset state when closing
  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen) {
        setInputValue('');
        resetQuery();
      }
      onOpenChange(newOpen);
    },
    [onOpenChange, resetQuery],
  );

  const closeAndNavigate = useCallback(
    (path: string) => {
      onOpenChange(false);
      navigate(path);
    },
    [onOpenChange, navigate],
  );

  const closeAndAction = useCallback(
    (action: () => void) => {
      onOpenChange(false);
      action();
    },
    [onOpenChange],
  );

  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            {/* Overlay */}
            <DialogPrimitive.Overlay asChild>
              <motion.div
                className="fixed inset-0 z-50 bg-black/50"
                variants={shouldAnimate ? overlayVariants : undefined}
                initial="initial"
                animate="animate"
                exit="exit"
              />
            </DialogPrimitive.Overlay>

            {/* Content */}
            <DialogPrimitive.Content asChild>
              <motion.div
                className="fixed left-[50%] top-[20%] z-50 w-full max-w-xl translate-x-[-50%] overflow-hidden rounded-xl border border-border bg-card shadow-2xl"
                variants={shouldAnimate ? scaleVariants : undefined}
                initial="initial"
                animate="animate"
                exit="exit"
              >
                <Command className="flex flex-col" shouldFilter={false} loop>
                  {/* Input */}
                  <div className="flex items-center border-b border-border px-4">
                    <Icon name="search" className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <Command.Input
                      value={inputValue}
                      onValueChange={handleInputChange}
                      placeholder={t('search.placeholder', 'Search or type a command...')}
                      className="flex-1 bg-transparent py-4 pl-3 text-sm outline-none placeholder:text-muted-foreground"
                    />
                    {isLoading && (
                      <Icon
                        name="progress_activity"
                        className="h-4 w-4 shrink-0 animate-spin text-muted-foreground"
                      />
                    )}
                  </div>

                  {/* Body */}
                  <Command.List className="max-h-[400px] overflow-y-auto p-2">
                    <Command.Empty className="py-8 text-center text-sm text-muted-foreground">
                      {isLoading ? (
                        <span>{t('search.loading', 'Searching...')}</span>
                      ) : (
                        <span>{t('search.noResults', 'No results found')}</span>
                      )}
                    </Command.Empty>

                    {/* Home Page - Quick Actions & Navigation */}
                    {page === 'home' && (
                      <>
                        {/* Quick Actions */}
                        <CommandGroup heading={t('commandPalette.actions', 'Actions')}>
                          {onCreateIssue && (
                            <CommandItem onSelect={() => closeAndAction(onCreateIssue)}>
                              <IconContainer className="bg-orange-100 dark:bg-orange-900/30">
                                <Icon
                                  name="add"
                                  className="h-4 w-4 text-orange-600 dark:text-orange-400"
                                />
                              </IconContainer>
                              <span>{t('commandPalette.createIssue', 'Create new issue')}</span>
                              <CommandShortcut>{modKey}⇧C</CommandShortcut>
                            </CommandItem>
                          )}
                          {onCreateProject && (
                            <CommandItem onSelect={() => closeAndAction(onCreateProject)}>
                              <IconContainer className="bg-blue-100 dark:bg-blue-900/30">
                                <Icon
                                  name="add"
                                  className="h-4 w-4 text-blue-600 dark:text-blue-400"
                                />
                              </IconContainer>
                              <span>{t('commandPalette.createProject', 'Create new project')}</span>
                              <CommandShortcut>{modKey}⇧P</CommandShortcut>
                            </CommandItem>
                          )}
                        </CommandGroup>

                        {/* Navigation */}
                        <CommandGroup heading={t('commandPalette.navigation', 'Navigation')}>
                          <CommandItem onSelect={() => closeAndNavigate('/')}>
                            <IconContainer className="bg-primary/10">
                              <Icon name="dashboard" className="h-4 w-4 text-primary" />
                            </IconContainer>
                            <span>{t('commandPalette.goToDashboard', 'Go to Dashboard')}</span>
                            <CommandShortcut>G then D</CommandShortcut>
                          </CommandItem>
                          <CommandItem onSelect={() => closeAndNavigate('/projects')}>
                            <IconContainer className="bg-blue-100 dark:bg-blue-900/30">
                              <Icon
                                name="folder"
                                className="h-4 w-4 text-blue-600 dark:text-blue-400"
                              />
                            </IconContainer>
                            <span>{t('commandPalette.goToProjects', 'Go to Projects')}</span>
                            <CommandShortcut>G then P</CommandShortcut>
                          </CommandItem>
                          <CommandItem onSelect={() => closeAndNavigate('/issues')}>
                            <IconContainer className="bg-orange-100 dark:bg-orange-900/30">
                              <Icon
                                name="bug_report"
                                className="h-4 w-4 text-orange-600 dark:text-orange-400"
                              />
                            </IconContainer>
                            <span>{t('commandPalette.goToIssues', 'Go to Issues')}</span>
                            <CommandShortcut>G then I</CommandShortcut>
                          </CommandItem>
                          <CommandItem onSelect={() => closeAndNavigate('/test-plans')}>
                            <IconContainer className="bg-purple-100 dark:bg-purple-900/30">
                              <Icon
                                name="checklist"
                                className="h-4 w-4 text-purple-600 dark:text-purple-400"
                              />
                            </IconContainer>
                            <span>{t('commandPalette.goToTestPlans', 'Go to Test Plans')}</span>
                          </CommandItem>
                          <CommandItem onSelect={() => closeAndNavigate('/go-live')}>
                            <IconContainer className="bg-emerald-100 dark:bg-emerald-900/30">
                              <Icon
                                name="rocket_launch"
                                className="h-4 w-4 text-emerald-600 dark:text-emerald-400"
                              />
                            </IconContainer>
                            <span>{t('commandPalette.goToGoLive', 'Go to Go-Live')}</span>
                          </CommandItem>
                        </CommandGroup>
                      </>
                    )}

                    {/* Search Results Page */}
                    {page === 'search' && hasResults && (
                      <>
                        {/* Projects */}
                        {results?.projects && results.projects.length > 0 && (
                          <CommandGroup heading={t('search.categories.projects', 'Projects')}>
                            {results.projects.map((project) => (
                              <CommandItem
                                key={project.id}
                                onSelect={() => closeAndNavigate(`/projects/${project.id}`)}
                              >
                                <IconContainer className="bg-blue-100 dark:bg-blue-900/30">
                                  <Icon
                                    name="folder"
                                    className="h-4 w-4 text-blue-600 dark:text-blue-400"
                                  />
                                </IconContainer>
                                <div className="min-w-0 flex-1">
                                  <p className="truncate font-medium">{project.name}</p>
                                  {project.tenant && (
                                    <p className="truncate text-xs text-muted-foreground">
                                      {project.tenant.name}
                                    </p>
                                  )}
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        )}

                        {/* Issues */}
                        {results?.issues && results.issues.length > 0 && (
                          <CommandGroup heading={t('search.categories.issues', 'Issues')}>
                            {results.issues.map((issue) => (
                              <CommandItem
                                key={issue.id}
                                onSelect={() => closeAndNavigate(`/issues/${issue.id}`)}
                              >
                                <IconContainer className="bg-orange-100 dark:bg-orange-900/30">
                                  <Icon
                                    name="bug_report"
                                    className="h-4 w-4 text-orange-600 dark:text-orange-400"
                                  />
                                </IconContainer>
                                <div className="min-w-0 flex-1">
                                  <p className="truncate font-medium">{issue.title}</p>
                                  <p className="truncate text-xs text-muted-foreground">
                                    {issue.project.name}
                                  </p>
                                </div>
                                <span
                                  className={cn(
                                    'shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium',
                                    getStatusColor(issue.status),
                                  )}
                                >
                                  {issue.status.replace('_', ' ')}
                                </span>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        )}

                        {/* Users */}
                        {results?.users && results.users.length > 0 && (
                          <CommandGroup heading={t('search.categories.users', 'Users')}>
                            {results.users.map((user) => (
                              <CommandItem
                                key={user.id}
                                onSelect={() => closeAndNavigate(`/admin/panel/users/${user.id}`)}
                              >
                                <Avatar className="h-8 w-8 shrink-0">
                                  <AvatarImage
                                    src={user.profilePictureUrl || undefined}
                                    alt={user.name}
                                  />
                                  <AvatarFallback className="text-xs">
                                    {getInitials(user.name)}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="min-w-0 flex-1">
                                  <p className="truncate font-medium">{user.name}</p>
                                  <p className="truncate text-xs text-muted-foreground">
                                    {user.email}
                                  </p>
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        )}
                      </>
                    )}
                  </Command.List>

                  {/* Footer */}
                  <div className="flex items-center justify-between border-t border-border px-4 py-2 text-xs text-muted-foreground">
                    <div className="flex items-center gap-4">
                      <span className="flex items-center gap-1">
                        <kbd className="rounded border border-border bg-muted px-1">↑↓</kbd>
                        <span>Navigate</span>
                      </span>
                      <span className="flex items-center gap-1">
                        <kbd className="rounded border border-border bg-muted px-1">↵</kbd>
                        <span>Select</span>
                      </span>
                    </div>
                    <span className="flex items-center gap-1">
                      <kbd className="rounded border border-border bg-muted px-1">esc</kbd>
                      <span>Close</span>
                    </span>
                  </div>
                </Command>
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}
